import React from 'react';
import { ChevronLeftIcon, ClockIcon, UserIcon, InfoIcon, LinkIcon, PaperclipIcon, MessageSquareIcon, ServerIcon, ExternalLinkIcon } from 'lucide-react';
import { AuditRecord, AuditActionType } from './AuditManagement';
import { StatusBadge } from '../../StatusBadge';
interface AuditDetailProps {
  audit: AuditRecord;
  onBack: () => void;
  locale: string;
}
export function AuditDetail({
  audit,
  onBack,
  locale
}: AuditDetailProps) {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  // Render action badge
  const renderActionBadge = (action: AuditActionType) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-14344" />;
      case 'Updated':
        return <StatusBadge status="warning" text="Updated" data-id="element-14345" />;
      case 'Deleted':
        return <StatusBadge status="error" text="Deleted" data-id="element-14346" />;
      case 'Approved':
        return <StatusBadge status="success" text="Approved" data-id="element-14347" />;
      case 'Rejected':
        return <StatusBadge status="error" text="Rejected" data-id="element-14348" />;
      case 'Viewed':
        return <StatusBadge status="default" text="Viewed" data-id="element-14349" />;
      case 'Logged In':
        return <StatusBadge status="info" text="Logged In" data-id="element-14350" />;
      case 'Logged Out':
        return <StatusBadge status="default" text="Logged Out" data-id="element-14351" />;
      case 'Password Changed':
        return <StatusBadge status="warning" text="Password Changed" data-id="element-14352" />;
      case 'Permission Changed':
        return <StatusBadge status="warning" text="Permission Changed" data-id="element-14353" />;
      case 'Configuration Changed':
        return <StatusBadge status="warning" text="Config Changed" data-id="element-14354" />;
      case 'License Activated':
        return <StatusBadge status="success" text="License Activated" data-id="element-14355" />;
      case 'License Deactivated':
        return <StatusBadge status="error" text="License Deactivated" data-id="element-14356" />;
      case 'Pass Used':
        return <StatusBadge status="info" text="Pass Used" data-id="element-14357" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-14358" />;
    }
  };
  // Determine if a value should be highlighted as added, removed, or unchanged
  const getValueClass = (oldValue: string | null, newValue: string | null) => {
    if (oldValue === null && newValue !== null) {
      return 'bg-green-50 text-green-800 px-2 py-1 rounded';
    } else if (oldValue !== null && newValue === null) {
      return 'bg-red-50 text-red-800 px-2 py-1 rounded';
    } else if (oldValue !== newValue) {
      return 'bg-yellow-50 text-yellow-800 px-2 py-1 rounded';
    }
    return '';
  };
  return <div className="space-y-6" data-id="element-14359">
      {/* Back button */}
      <div data-id="element-14360">
        <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14361">
          <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-14362" />
          Back to Audit Log
        </button>
      </div>
      {/* Audit details card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-14363">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-14364">
          <div data-id="element-14365">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14366">
              Audit Record Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-14367">
              Full information about this system action.
            </p>
          </div>
          <div className="flex items-center" data-id="element-14368">
            {renderActionBadge(audit.action)}
          </div>
        </div>
        {/* Object reference */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-14369">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-14370">
            <div className="sm:col-span-1" data-id="element-14371">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14372">
                Object Reference
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-14373">
                <div className="flex items-center" data-id="element-14374">
                  <span className="font-medium text-indigo-600 mr-2" data-id="element-14375">
                    {audit.module} #{audit.objectId}
                  </span>
                  <button className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-14376">
                    <ExternalLinkIcon className="h-3 w-3 mr-1" data-id="element-14377" />
                    View
                  </button>
                </div>
                <span className="block text-gray-500" data-id="element-14378">{audit.objectName}</span>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-14379">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14380">Timestamp</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-14381">
                <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-14382" />
                {formatTimestamp(audit.timestamp)}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-14383">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14384">User</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-14385">
                <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-14386" />
                <div data-id="element-14387">
                  <span data-id="element-14388">{audit.userName}</span>
                  <span className="ml-2 text-xs text-gray-500" data-id="element-14389">
                    ({audit.userType})
                  </span>
                </div>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-14390">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14391">IP Address</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-14392">
                <ServerIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-14393" />
                {audit.ipAddress || 'Not recorded'}
              </dd>
            </div>
            {(audit.entityName || audit.siteName) && <div className="sm:col-span-2" data-id="element-14394">
                <dt className="text-sm font-medium text-gray-500" data-id="element-14395">Context</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-14396">
                  {audit.entityName && <span className="mr-2" data-id="element-14397">Entity: {audit.entityName}</span>}
                  {audit.siteName && <span data-id="element-14398">Site: {audit.siteName}</span>}
                </dd>
              </div>}
            <div className="sm:col-span-2" data-id="element-14399">
              <dt className="text-sm font-medium text-gray-500" data-id="element-14400">Changes</dt>
              {audit.changes.length === 0 ? <dd className="mt-1 text-sm text-gray-500 italic" data-id="element-14401">
                  No field changes recorded for this action.
                </dd> : <dd className="mt-1" data-id="element-14402">
                  <div className="bg-gray-50 overflow-hidden sm:rounded-md border border-gray-200" data-id="element-14403">
                    <ul className="divide-y divide-gray-200" data-id="element-14404">
                      {audit.changes.map((change, index) => <li key={index} className="px-4 py-3" data-id="element-14405">
                          <div className="flex flex-col sm:flex-row sm:justify-between" data-id="element-14406">
                            <div className="text-sm font-medium text-gray-900 mb-1 sm:mb-0" data-id="element-14407">
                              {change.field}
                            </div>
                            <div className="flex-1 sm:ml-4 flex flex-col sm:flex-row sm:items-center text-sm" data-id="element-14408">
                              {change.oldValue !== null && <div className={`sm:text-right ${change.oldValue !== change.newValue ? 'line-through text-red-600' : ''}`} data-id="element-14409">
                                  {change.oldValue || '<empty>'}
                                </div>}
                              {change.oldValue !== null && change.newValue !== null && <div className="hidden sm:block mx-2" data-id="element-14410">→</div>}
                              {change.newValue !== null && <div className={getValueClass(change.oldValue, change.newValue)} data-id="element-14411">
                                  {change.newValue || '<empty>'}
                                </div>}
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>
                </dd>}
            </div>
            {audit.relatedNotes && audit.relatedNotes.length > 0 && <div className="sm:col-span-2" data-id="element-14412">
                <dt className="text-sm font-medium text-gray-500" data-id="element-14413">
                  Related Notes
                </dt>
                <dd className="mt-1" data-id="element-14414">
                  <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-14415">
                    {audit.relatedNotes.map((note, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-14416">
                        <div className="w-0 flex-1 flex items-center" data-id="element-14417">
                          <MessageSquareIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-14418" />
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-14419">
                            {note}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-14420">
                          <button className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-14421">
                            View
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </dd>
              </div>}
            {audit.relatedAttachments && audit.relatedAttachments.length > 0 && <div className="sm:col-span-2" data-id="element-14422">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-14423">
                    Related Attachments
                  </dt>
                  <dd className="mt-1" data-id="element-14424">
                    <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-14425">
                      {audit.relatedAttachments.map((attachment, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-14426">
                          <div className="w-0 flex-1 flex items-center" data-id="element-14427">
                            <PaperclipIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-14428" />
                            <span className="ml-2 flex-1 w-0 truncate" data-id="element-14429">
                              {attachment}
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0" data-id="element-14430">
                            <button className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-14431">
                              Download
                            </button>
                          </div>
                        </li>)}
                    </ul>
                  </dd>
                </div>}
          </dl>
        </div>
      </div>
    </div>;
}